import React, { useContext, useEffect, useState } from 'react';
import APIClient from '../../helpers/APIClient';
import SubscriptionItem from '../SubscriptionList/SubscriptionItem/SubscriptionItem';
import localization from '../../data/localization/en-US.json';
import arrowLeft from '../../assets/images/icons/arrow-left.svg';
import { subscriptionContext } from '../../context/subscriptionContext';
import { sortSubscriptionList } from '../../helpers/util/utils';
import Styles from './EditSubscriptions.module.scss';
import NotificationBadge from '../NotificationBadge/NotificationBadge';
import Checkmark from '../../assets/images/icons/checkmark.svg';

const EditSubscriptions = (props) => {
  const { modalOpen, toggleModal } = props;
  const [unsubscribeAllCheckbox, setUnsubscribeAllCheckbox] = useState(null);
  const [subscribeList, setSubscribeList] = useState([]);
  const [unsubscribeList, setUnsubscribeList] = useState([]);
  const [subscriptionSuccessMessage, setSubscriptionSuccessMessage] = useState(null);
  const subscriptions = useContext(subscriptionContext);
  const { state, dispatch } = subscriptions;
  const { data, globallySuppressed } = state;
  const [notificationMessageOpen, setNotificationMessageOpen] = useState(false);

  const getSubscriptions = () => {
    APIClient.getSubscriptions((status, res) => {
      if (status === 200 && res.data) {
        dispatch({
          type: 'SET_SUBSCRIPTIONS',
          data: res.data,
          globallySuppressed: res?.data?.global?.suppressed ?? null,
        });
      }
    });
  };

  useEffect(() => {
    setUnsubscribeAllCheckbox(globallySuppressed);
  }, [globallySuppressed]);

  const updateSubscribeList = (id) => {
    const isInSubscribeList = data.groups.find((sub) => sub.id === id);
    // only add to subscribe list if it wasn't orignially subscribed to
    if (isInSubscribeList.suppressed === true) {
      setSubscribeList([
        ...subscribeList,
        id,
      ]);
    }
    const index = unsubscribeList.indexOf(id);
    if (index >= 0) {
      const removeUnsub = [...unsubscribeList];
      removeUnsub.splice(index, 1);
      setUnsubscribeList(removeUnsub);
    }
  };

  const updateUnsubscribeList = (id) => {
    const isInUnsubscribeList = data.groups.find((sub) => sub.id === id);
    // only add to unsubscribe list if it wasn't orignially unsubscribed to
    if (isInUnsubscribeList.suppressed === false) {
      setUnsubscribeList([
        ...unsubscribeList,
        id,
      ]);
    }
    const index = subscribeList.indexOf(id);
    if (index >= 0) {
      const removeSub = [...subscribeList];
      removeSub.splice(index, 1);
      setSubscribeList(removeSub);
    }
  };

  const updateSubscriptions = () => {
    const subscribe = new Promise((resolve, reject) => {
      if (subscribeList.length === 0) {
        resolve(true);
      } else {
        subscribeList.forEach((id, i) => {
          APIClient.subscribe(id, (status) => {
            if (status !== 200) {
              resolve(false);
            }
            if (i === subscribeList.length - 1) {
              resolve(true);
            }
          });
        });
      }
    });

    const unsubscribe = new Promise((resolve, reject) => {
      if (unsubscribeList.length === 0) {
        resolve(true);
      } else {
        unsubscribeList.forEach((id, j) => {
          APIClient.unsubscribe(id, (status) => {
            if (status !== 200) {
              resolve(false);
            }
            if (j === unsubscribeList.length - 1) {
              resolve(true);
            }
          });
        });
      }
    });

    Promise.all([subscribe, unsubscribe]).then((res) => {
      if (res.includes(false)) {
        setSubscriptionSuccessMessage({
          text: localization?.errorMessages?.subscriptionError,
          icon: null,
        });
      } else {
        setSubscriptionSuccessMessage({
          text: localization?.successMessage,
          icon: Checkmark,
        });
      }

      getSubscriptions();
      setNotificationMessageOpen(true);
    })
      .catch(() => {
        throw new Error();
      });
  };

  const handleSubscriptionCall = () => {
    if (!globallySuppressed) {
      if (unsubscribeAllCheckbox) {
        APIClient.unsubscribeAll((status) => {
          if (status === 200) {
            setSubscriptionSuccessMessage({
              text: localization?.unsubscribeAllMessage,
              icon: Checkmark,
            });
            setNotificationMessageOpen(true);
            getSubscriptions();
          } else {
            setSubscriptionSuccessMessage({
              text: localization.errorMessages.unsubscribeAllError,
              icon: null,
            });
          }
        });
        return;
      }
      updateSubscriptions();
    }

    if (globallySuppressed && !unsubscribeAllCheckbox) {
      APIClient.subscribeAll((status) => {
        if (status === 200) {
          updateSubscriptions();
          setSubscriptionSuccessMessage({
            text: localization.successMessage,
            icon: Checkmark,
          });
        } else {
          setSubscriptionSuccessMessage({
            text: localization.errorMessages.subscribeAllError,
            icon: null,
          });
        }
      });
    }
  };

  useEffect(() => {
    if (notificationMessageOpen) {
      setTimeout(() => {
        setNotificationMessageOpen(false);
      }, 5000);
    }
  }, [notificationMessageOpen]);

  return (
    <div className={Styles.pingNationContent}>
      <div className={Styles.btnContainer}>
        <button
          className={Styles.backToAccountBtn}
          type='button'
          onClick={() => {
            toggleModal();
          }}
        >
          <img src={arrowLeft} alt='arrow-left' />
          {localization.backToMyAccount}
        </button>
      </div>
      <div className={Styles.tileHeader}>
        {localization.subscriptionHeaderText}
      </div>
      <div className={Styles.editScreenMessage}>
        {localization.editScreenMessage
          && localization.editScreenMessage.map((message, i) => {
            return (
              <div key={`editMessage-${i}`} className={Styles.editScreenMessage}>{message}</div>
            );
          })
        }
      </div>
      <div className={Styles.subscriptionItems}>
        {data?.groups
          && sortSubscriptionList(data.groups).map((item, i) => {
            return (
              <div key={`${item.name?.replace(' ', '')}-${item.id}`}>
                <SubscriptionItem
                  item={item}
                  isEditing={true}
                  globallySuppressed={globallySuppressed}
                  unsubscribeAllCheckbox={unsubscribeAllCheckbox}
                  updateSubscribeList={updateSubscribeList}
                  updateUnsubscribeList={updateUnsubscribeList}
                />
              </div>
            );
          })
        }
      </div>
      <div className={Styles.unsubscribeAll}>
        <input
          id='unsubscribeAllCheck'
          type='checkbox'
          defaultChecked={globallySuppressed}
          onClick={() => setUnsubscribeAllCheckbox(!unsubscribeAllCheckbox)}
        />
        <label
          forhtml='unsubscribeAllCheck'
          onClick={() => {
            const checkboxId = document.getElementById('unsubscribeAllCheck');
            if (checkboxId) {
              checkboxId.checked = !unsubscribeAllCheckbox;
              setUnsubscribeAllCheckbox(!unsubscribeAllCheckbox);
            }
          }}
        >
          {localization.unsubscribeAllLabel}
        </label>
      </div>
      {data
        && (
          <div className={Styles.display}>
            <button
              className={Styles.updateBtn}
              type='button'
              onClick={() => handleSubscriptionCall()}
            >
              {localization.updateBtnText}
            </button>
            <NotificationBadge
              isOpen={notificationMessageOpen}
              message={subscriptionSuccessMessage}
              setNotificationMessageOpen={setNotificationMessageOpen}
            />
          </div>
        )
      }
    </div>
  );
};

export default EditSubscriptions;
